import React, { useRef, useEffect, useMemo } from "react";
import { Sprite, Container } from "@pixi/react";
import { useButtonHover } from "../../../../Hooks/useButtonHover";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { openMenu, closeMenu } from "../../../../State/actions";
import {
  selectMenuActive,
  selectCriticalAnimationPlaying,
  selectIsSpinning,
  selectHoldAndWinActive,
  selectAutoPlayActive,
  selectUserInterfaceDisabled,
  selectFreeSpinActive,
} from "../../../../State/selectors";
import { SOUND_NAME } from "../../../../sounds";
import { getUITexture } from "../../../../assets";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { PRIMARY_USER_INTERFACE_BUTTON_CONFIG } from "../../../../textureConfigs";
import { PixiSettings } from "../../../../pixiSettings";
import { soundManager } from "../../../../SoundManager";

// Position config — extracted for easy tweaking
const MENU_BUTTON_POSITION_CONFIG = {
  [PixiSettings.DEVICE.MOBILE]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      x: (toolbar, buttonConfig) => toolbar.width - buttonConfig.width * 1.0,
      y: (toolbar) => toolbar.height / 2,
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      x: (toolbar, buttonConfig) => toolbar.width - buttonConfig.width * 1.0,
      y: (toolbar) => toolbar.height / 2,
    },
  },
  [PixiSettings.DEVICE.TABLET]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      x: (toolbar, buttonConfig) => toolbar.width - buttonConfig.width * 1.0,
      y: (toolbar) => toolbar.height / 2,
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      x: (toolbar, buttonConfig) => toolbar.width - buttonConfig.width * 1.0,
      y: (toolbar) => toolbar.height / 2,
    },
  },
  [PixiSettings.DEVICE.DESKTOP]: {
    x: (toolbar, buttonConfig) => toolbar.width - buttonConfig.width,
    y: (toolbar) => toolbar.height / 2,
  },
};

const ButtonMenu = ({ toolbar, position: positionOverride }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { getResponsiveConfig, device, orientation } = useDimensions();
  const menuActive = useSelector(selectMenuActive);
  const criticalAnimationPlaying = useSelector(selectCriticalAnimationPlaying);
  const isSpinning = useSelector(selectIsSpinning);
  const holdAndWinActive = useSelector(selectHoldAndWinActive);
  const autoPlayActive = useSelector(selectAutoPlayActive);
  const userInterfaceDisabled = useSelector(selectUserInterfaceDisabled);

  const freeSpinActive = useSelector(selectFreeSpinActive);

  const { hoverContainerRef, buttonRef, handlePointerOver, handlePointerOut, triggerPress } = useButtonHover();
  const isProcessingClickRef = useRef(false);
  const debounceTimeoutRef = useRef(null);

  const isDisabled =
    userInterfaceDisabled ||
    criticalAnimationPlaying ||
    isSpinning ||
    holdAndWinActive ||
    autoPlayActive ||
    freeSpinActive;

  const texture = getUITexture("icon_menu");

  const buttonConfig = useMemo(
    () => getResponsiveConfig(PRIMARY_USER_INTERFACE_BUTTON_CONFIG, true),
    [getResponsiveConfig],
  );

  // Position using config
  const buttonPosition = useMemo(() => {
    const deviceConfig = MENU_BUTTON_POSITION_CONFIG[device];
    const posConfig = deviceConfig?.[orientation] ?? deviceConfig;
    return {
      x: posConfig.x(toolbar, buttonConfig),
      y: posConfig.y(toolbar, buttonConfig),
    };
  }, [device, orientation, toolbar, buttonConfig]);

  // Allow parent to override position
  const finalPosition = positionOverride || buttonPosition;

  const handleClick = () => {
    if (isDisabled) return;

    if (isProcessingClickRef.current) return;

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    isProcessingClickRef.current = true;

    soundManager.play(SOUND_NAME.button_click);

    triggerPress();

    if (menuActive) {
      dispatch(closeMenu());
    } else {
      dispatch(openMenu());
    }

    debounceTimeoutRef.current = setTimeout(() => {
      isProcessingClickRef.current = false;
    }, 500);
  };

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);



  return (
    <Container
      name="secondary_backport_button_menu"
      x={finalPosition.x}
      y={finalPosition.y}
      zIndex={2}
    >
      <Container ref={hoverContainerRef}>
        <Sprite
          ref={buttonRef}
          texture={texture}
          name="button_menu_icon"
          anchor={0.5}
          width={buttonConfig.width}
          height={buttonConfig.height}
          x={0}
          y={0}
          eventMode={isDisabled ? "none" : "static"}
          cursor={isDisabled ? "default" : "pointer"}
          alpha={isDisabled ? 0.5 : 1}
          onpointertap={handleClick}
          onpointerover={handlePointerOver}
          onpointerout={handlePointerOut}
          accessible={true}
          accessibleTitle={t("open_menu", { ns: "common" })}
          accessibleHint={t("open_menu", { ns: "common" })}
          resolution={2}
        />
      </Container>
    </Container>
  );
};

export default ButtonMenu;
